import { ApiClient } from './client';
import { setAuthToken } from '@/lib/utils';
import { publicEndpoints } from '@/lib/endpoints';
import type { AuthTokenResponse } from '@/lib/types/authentication';
import { loginApi } from '@/lib/api/login';
import { isTokenExpired } from '../types/jwt';
import { store } from "@/lib/store/store";
import { loginExpired } from "@/lib/store/auth/authSlice";


const PUBLIC_BASE_URL = 'https://' + import.meta.env.VITE_BACKEND_CUSTOM_DOMAIN_NAME + '/api-frontend/';
// const PUBLIC_BASE_URL = 'http://' + import.meta.env.VITE_BACKEND_CUSTOM_DOMAIN_NAME + '/api-frontend/';

const client = new ApiClient(PUBLIC_BASE_URL);

export const ensureGuestToken = async () => {
  // Skip token logic during server-side rendering / static generation
  if (typeof window === "undefined") {
    return;
  }
  
  let auth_token = localStorage.getItem('auth_token')
  if (auth_token && isTokenExpired(auth_token)) {
    store.dispatch(loginExpired());
    auth_token = null;
    window.location.href = "/login";
  }
  if (!auth_token) {
    const response = await loginApi.post<AuthTokenResponse>(publicEndpoints.auth.login, {
      is_guest: true,
      username: null,

      email: null,
      password: null
    });
    if (!response.data) return;
    const token = response.data.token;

    if (token && !isTokenExpired(token)) setAuthToken(token);
    return;
  }
};

export const publicApi = {
  get: async <T>(endpoint: string) => {
    await ensureGuestToken();
    return client.get<T>(endpoint);
  },
  post: async <T>(endpoint: string, data?: unknown) => {
    // Don't call ensureGuestToken for login requests to avoid interference
    if (endpoint === publicEndpoints.auth.login && data && typeof data === 'object' && 'is_guest' in data && !data.is_guest)
      return client.post<T>(endpoint, data);
    await ensureGuestToken();
    return client.post<T>(endpoint, data);
  },
  put: async <T>(endpoint: string, data?: unknown) => {
    await ensureGuestToken();
    return client.put<T>(endpoint, data);
  },
  patch: async <T>(endpoint: string, data?: unknown) => {
    await ensureGuestToken();
    return client.patch<T>(endpoint, data);
  },
  delete: async <T>(endpoint: string) => {
    await ensureGuestToken();
    return client.delete<T>(endpoint);
  },
  /**
   * POST a raw binary body (e.g. a File/Blob) to `endpoint`.
   * Handles guest-token refresh and auth header injection via the shared ApiClient.
   */
  postBinary: async <T>(endpoint: string, body: BodyInit, contentType: string) => {
    await ensureGuestToken();
    return client.postRaw<T>(endpoint, body, contentType);
  },
  postForm: async <T>(endpoint: string, body: FormData) => {
    await ensureGuestToken();
    return client.postForm<T>(endpoint, body);
  },
};